import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Button, IconButton, Chip,
  List, ListItem, ListItemButton, ListItemText,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Alert, InputAdornment, Stack, Tooltip, Paper,
  Tabs, Tab, Switch, FormControlLabel, FormControl, InputLabel, Select,
  MenuItem, Collapse, TablePagination, Autocomplete
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, Save as SaveIcon,
  Edit as EditIcon, Search as SearchIcon,
  CloudUpload as UploadIcon, LocalOffer as TierIcon,
  Inventory2 as SkuIcon,
  KeyboardArrowDown as ExpandIcon, KeyboardArrowUp as CollapseIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useBrand } from '../../context/BrandContext';
import {
  listPriceTiers, getPriceTier,
  upsertPriceTierItems, deletePriceTierItem,
  listPriceTiersBySku, getPriceTierSkuTiers, searchTierProducts
} from '../../services/inventoryService';
import NewPriceTierDialog from './NewPriceTierDialog';
import PriceTierBulkUpload from './PriceTierBulkUpload';

function timeAgo(iso) {
  if (!iso) return '';
  const d = new Date(String(iso).replace(' ', 'T') + 'Z');
  if (Number.isNaN(d.getTime())) return '';
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
}

/**
 * Product typeahead for a NEW tier row: search the company product master by
 * SKU or name so the row is a real product and its name shows immediately
 * (Rishabh 2026-07-15 — typing a SKU showed no name). Company-wide search; on
 * pick it fills sku + product_name. Free-solo so a paste of an exact SKU still
 * works even if the typeahead hasn't matched it yet.
 */
function ProductSkuPicker({ value, onPick }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const q = String(value || '').trim();
    if (q.length < 2) { setOptions([]); return; }
    let active = true;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await searchTierProducts(q);
        if (active) setOptions(res.data || []);
      } catch { if (active) setOptions([]); }
      finally { if (active) setLoading(false); }
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [value]);

  return (
    <Autocomplete
      freeSolo
      size="small"
      fullWidth
      options={options}
      loading={loading}
      inputValue={value || ''}
      filterOptions={(x) => x}
      getOptionLabel={(o) => (typeof o === 'string' ? o : (o.sku || ''))}
      isOptionEqualToValue={(o, v) => o.sku === (v?.sku ?? v)}
      onInputChange={(e, v, reason) => { if (reason === 'input' || reason === 'clear') onPick(v, null); }}
      onChange={(e, v) => { if (v && typeof v === 'object') onPick(v.sku, v.product_name || ''); }}
      renderOption={(props, o) => (
        <li {...props} key={o.sku}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontFamily: 'monospace', fontSize: 13 }}>{o.sku}</Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {o.product_name || '(no name)'}{o.product_level ? ` · ${o.product_level}` : ''}
            </Typography>
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params} variant="standard" placeholder="Search SKU or name…"
          inputProps={{ ...params.inputProps, style: { fontFamily: 'monospace', fontSize: 13 } }}
        />
      )}
    />
  );
}

/**
 * Price Tier admin — master/detail.
 * Left pane: tier list with search and live counts.
 * Right pane: SKU price grid for the selected tier with inline edit and bulk upload.
 */
export default function PriceTierAdmin() {
  const [tiers, setTiers] = useState([]);
  const [selectedTierId, setSelectedTierId] = useState(null);
  const [tierDetail, setTierDetail] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [tierSearch, setTierSearch] = useState('');
  const [skuSearch, setSkuSearch] = useState('');
  // View 0 = By Customer (master-detail), View 1 = By SKU (redesign 2026-07-03).
  const [view, setView] = useState(0);
  // Lazy-mount By SKU on first visit, then KEEP it mounted (display:none) so
  // tabbing away and back preserves search/filters/pagination/expansion.
  const [bySkuVisited, setBySkuVisited] = useState(false);
  useEffect(() => { if (view === 1) setBySkuVisited(true); }, [view]);
  // Tiers whose EVERY linked customer is inactive are noise for day-to-day
  // pricing — hidden by default with a visible count + toggle (never silent).
  const [showInactiveCustomerTiers, setShowInactiveCustomerTiers] = useState(false);

  // NewPriceTierDialog handles both create and edit
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTier, setDialogTier] = useState(null); // null = create

  // Bulk-upload dialog (existing tier)
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadRows, setUploadRows] = useState([]);
  const [uploading, setUploading] = useState(false);

  const loadTiers = async () => {
    try {
      setLoading(true);
      const res = await listPriceTiers();
      setTiers(res.data || []);
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setLoading(false); }
  };

  const loadTier = async (id) => {
    if (!id) { setTierDetail(null); setItems([]); return; }
    try {
      setItemsLoading(true);
      const res = await getPriceTier(id);
      setTierDetail(res.data);
      // The CIN7 import cached product names into notes; the Product column
      // now shows the live name, so a notes value that just repeats the name
      // is stripped at load. The blank only persists if the row is edited —
      // which is exactly the gradual cleanup we want.
      setItems((res.data?.items || []).map(it => {
        const noteIsName = it.notes && it.product_name
          && String(it.notes).trim().toLowerCase() === String(it.product_name).trim().toLowerCase();
        return { ...it, notes: noteIsName ? '' : it.notes, _dirty: false };
      }));
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setItemsLoading(false); }
  };

  useEffect(() => { loadTiers(); }, []);
  useEffect(() => { loadTier(selectedTierId); }, [selectedTierId]);

  const totalSkuCount = useMemo(
    () => tiers.reduce((a, t) => a + (Number(t.item_count) || 0), 0),
    [tiers]
  );

  // A tier is "inactive-customer" when it HAS customers and none is active.
  // Tiers with no customer link are generic price lists — always shown.
  const isInactiveCustomerTier = (t) =>
    Number(t.n_customers) > 0 && Number(t.n_active_customers) === 0;

  // Hidden while BROWSING: deactivated tiers get the same treatment as
  // inactive-customer tiers (Rishabh 2026-07-06 — a retired customer's tier
  // should leave the list, not sit dimmed in it). Search still finds both.
  const isHiddenTier = (t) => !Number(t.is_active) || isInactiveCustomerTier(t);

  const inactiveCustomerTierCount = useMemo(
    () => tiers.filter(isHiddenTier).length,
    [tiers]
  );

  const filteredTiers = useMemo(() => {
    const q = tierSearch.trim().toLowerCase();
    // Searching overrides the hide-inactive filter: hiding is for BROWSING;
    // a search means "find it wherever it is" — otherwise typing "Zalando"
    // (all-inactive customers) with the default toggle off finds nothing and
    // reads as data loss (QA catch 2026-07-03). Hidden tiers found by search
    // still show their grey "0/N active" chip.
    if (q) {
      return tiers.filter(t =>
        String(t.tier_name || '').toLowerCase().includes(q) ||
        String(t.tier_code || '').toLowerCase().includes(q) ||
        String(t.currency || '').toLowerCase().includes(q) ||
        (t.customer_names || []).some(n => String(n).toLowerCase().includes(q))
      );
    }
    return showInactiveCustomerTiers ? tiers : tiers.filter(t => !isHiddenTier(t));
  }, [tiers, tierSearch, showInactiveCustomerTiers]);

  const dirtyCount = useMemo(() => items.filter(i => i._dirty || i._new).length, [items]);

  const filteredItems = useMemo(() => {
    const q = skuSearch.trim().toLowerCase();
    if (!q) return items;
    return items.filter(it =>
      String(it.sku || '').toLowerCase().includes(q) ||
      String(it.product_name || '').toLowerCase().includes(q) ||
      String(it.notes || '').toLowerCase().includes(q)
    );
  }, [items, skuSearch]);

  const handleAddItemRow = () => {
    const newRow = { id: `new-${Date.now()}`, sku: '', unit_price: 0, _new: true, _dirty: true, is_active: 1 };
    setItems([newRow, ...items]);
    setSkuSearch('');
  };

  const handleItemChange = (id, field, value) => {
    setItems(items.map(it => it.id === id ? { ...it, [field]: value, _dirty: true } : it));
  };

  // Merge several fields in one update (functional form avoids the stale-closure
  // clobber you get from two back-to-back handleItemChange calls). Used by the
  // product picker to set sku + product_name together.
  const handleItemPatch = (id, patch) => {
    setItems(prev => prev.map(it => it.id === id ? { ...it, ...patch, _dirty: true } : it));
  };

  const handleSaveItems = async () => {
    try {
      const dirty = items.filter(i => (i._dirty || i._new) && String(i.sku).trim());
      if (dirty.length === 0) { setSuccess('Nothing to save'); return; }
      const payload = dirty.map(i => ({
        sku: String(i.sku).trim(),
        unit_price: parseFloat(i.unit_price) || 0,
        notes: i.notes || '',
      }));
      await upsertPriceTierItems(selectedTierId, payload);
      setSuccess(`Saved ${payload.length} item${payload.length === 1 ? '' : 's'}`);
      await loadTier(selectedTierId);
      await loadTiers();
    } catch (e) { setError(e.response?.data?.error || e.message); }
  };

  const handleDeleteItem = async (item) => {
    if (item._new) {
      setItems(items.filter(i => i.id !== item.id));
      return;
    }
    if (!window.confirm(`Delete price for ${item.sku}?`)) return;
    try {
      await deletePriceTierItem(selectedTierId, item.id);
      await loadTier(selectedTierId);
      await loadTiers();
    } catch (e) { setError(e.response?.data?.error || e.message); }
  };

  const handleBulkUploadSubmit = async () => {
    if (uploadRows.length === 0) return;
    setUploading(true);
    try {
      await upsertPriceTierItems(selectedTierId, uploadRows);
      setSuccess(`Uploaded ${uploadRows.length} row${uploadRows.length === 1 ? '' : 's'}`);
      setUploadOpen(false);
      setUploadRows([]);
      await loadTier(selectedTierId);
      await loadTiers();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5 }}>Price Tiers</Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Chip size="small" icon={<TierIcon />} label={`${tiers.length} tier${tiers.length === 1 ? '' : 's'}`} />
            <Chip size="small" icon={<SkuIcon />} label={`${totalSkuCount} SKU price${totalSkuCount === 1 ? '' : 's'}`} variant="outlined" />
            <Typography variant="body2" color="text.secondary">
              Per-retailer pricing. Customer rows point here via <code>price_tier_id</code>.
            </Typography>
          </Stack>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setDialogTier(null); setDialogOpen(true); }}>
          New Tier
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Tabs value={view} onChange={(e, v) => setView(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tab label="By Customer" />
        <Tab label="By SKU" />
      </Tabs>

      {bySkuVisited && (
        <Box sx={view === 1 ? undefined : { display: 'none' }}>
          <BySkuView onOpenTier={(tierId) => { setView(0); setSelectedTierId(tierId); }} />
        </Box>
      )}

      <Grid container spacing={2} sx={view === 1 ? { display: 'none' } : undefined}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <TextField
                size="small" fullWidth placeholder="Search tiers or customers…"
                value={tierSearch} onChange={(e) => setTierSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                sx={{ mb: 0.5 }}
              />
              {inactiveCustomerTierCount > 0 && (
                <FormControlLabel
                  sx={{ mb: 0.5, ml: 0 }}
                  control={
                    <Switch
                      size="small"
                      checked={showInactiveCustomerTiers}
                      onChange={(e) => setShowInactiveCustomerTiers(e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="caption" color="text.secondary">
                      Show {inactiveCustomerTierCount} hidden tier{inactiveCustomerTierCount > 1 ? 's' : ''} (inactive / inactive customers)
                    </Typography>
                  }
                />
              )}
              {filteredTiers.length === 0 ? (
                <Box textAlign="center" py={3}>
                  <TierIcon sx={{ fontSize: 36, color: 'text.disabled' }} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {tiers.length === 0 ? 'No price tiers yet' : 'No tiers match your search'}
                  </Typography>
                  {tiers.length === 0 && (
                    <Button size="small" startIcon={<AddIcon />} sx={{ mt: 1 }} onClick={() => { setDialogTier(null); setDialogOpen(true); }}>
                      Create your first tier
                    </Button>
                  )}
                </Box>
              ) : (
                <List dense disablePadding sx={{ maxHeight: 600, overflowY: 'auto' }}>
                  {filteredTiers.map(t => (
                    <ListItem key={t.id} disablePadding>
                      <ListItemButton
                        selected={selectedTierId === t.id}
                        onClick={() => setSelectedTierId(t.id)}
                        sx={{ borderRadius: 1, mb: 0.5, opacity: t.is_active ? 1 : 0.55 }}
                      >
                        <ListItemText
                          primary={
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <Typography variant="body2" fontWeight={selectedTierId === t.id ? 700 : 500} noWrap>
                                {t.tier_name || `Tier #${t.id}`}
                              </Typography>
                              {t.tier_code && <Typography variant="caption" color="text.secondary">· {t.tier_code}</Typography>}
                            </Stack>
                          }
                          secondary={
                            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25, flexWrap: 'wrap' }}>
                              <Chip label={t.currency || 'currency not set'} size="small" color={t.currency ? 'default' : 'warning'} sx={{ height: 18, fontSize: 10 }} />
                              <Chip label={`${t.item_count || 0} SKU${(t.item_count || 0) === 1 ? '' : 's'}`} size="small" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                              {Number(t.n_customers) > 0 && (
                                <Chip
                                  label={`${t.n_active_customers}/${t.n_customers} active`}
                                  size="small"
                                  color={Number(t.n_active_customers) > 0 ? 'success' : 'default'}
                                  variant="outlined"
                                  sx={{ height: 18, fontSize: 10, opacity: Number(t.n_active_customers) > 0 ? 1 : 0.7 }}
                                />
                              )}
                              {!t.is_active && <Chip label="inactive" size="small" sx={{ height: 18, fontSize: 10 }} />}
                              {t.updated_at && (
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto !important' }}>
                                  {timeAgo(t.updated_at)}
                                </Typography>
                              )}
                            </Stack>
                          }
                          secondaryTypographyProps={{ component: 'div' }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              {!tierDetail ? (
                <Box textAlign="center" py={6}>
                  <SkuIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography color="text.secondary">
                    Select a tier on the left to view and edit its SKU prices.
                  </Typography>
                </Box>
              ) : (
                <>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="h6">{tierDetail.tier_name}</Typography>
                        <Tooltip title="Edit tier header">
                          <IconButton size="small" onClick={() => { setDialogTier(tierDetail); setDialogOpen(true); }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {tierDetail.tier_code ? `${tierDetail.tier_code} · ` : ''}{tierDetail.currency || 'currency not set'} · {items.length} item{items.length === 1 ? '' : 's'}
                        {tierDetail.notes && ` · ${tierDetail.notes}`}
                      </Typography>
                      {(tierDetail.customers || []).length > 0 && (
                        <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}>
                          {(tierDetail.customers || []).slice(0, 6).map((c, i) => (
                            <Chip
                              key={`${c.customer_name}-${i}`}
                              label={c.customer_name}
                              size="small"
                              variant="outlined"
                              color={Number(c.is_active) === 1 ? 'success' : 'default'}
                              sx={{ height: 20, fontSize: 11, opacity: Number(c.is_active) === 1 ? 1 : 0.6 }}
                            />
                          ))}
                          {(tierDetail.customers || []).length > 6 && (
                            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                              +{tierDetail.customers.length - 6} more
                            </Typography>
                          )}
                        </Stack>
                      )}
                    </Box>
                    <Stack direction="row" spacing={1}>
                      <Button size="small" startIcon={<AddIcon />} onClick={handleAddItemRow}>Add Row</Button>
                      <Button size="small" startIcon={<UploadIcon />} onClick={() => { setUploadRows([]); setError(null); setUploadOpen(true); }}>
                        Upload CSV / Excel
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSaveItems}
                        disabled={dirtyCount === 0}
                      >
                        Save {dirtyCount > 0 ? `(${dirtyCount})` : ''}
                      </Button>
                    </Stack>
                  </Box>

                  <TextField
                    size="small" fullWidth placeholder="Search SKU, product name or notes…"
                    value={skuSearch} onChange={(e) => setSkuSearch(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                    sx={{ mb: 1.5 }}
                  />

                  {itemsLoading ? <CircularProgress /> : (
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 600 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ width: '22%' }}>SKU</TableCell>
                            {/* Product name comes from a live product-master
                                join — it was previously (wrongly) shown under
                                "Notes" via names the CIN7 import cached there. */}
                            <TableCell sx={{ width: '30%' }}>Product</TableCell>
                            <TableCell align="right" sx={{ width: '16%' }}>Unit Price ({tierDetail.currency || 'currency not set'})</TableCell>
                            <TableCell>Notes</TableCell>
                            <TableCell align="right" sx={{ width: 50 }} />
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredItems.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                {items.length === 0
                                  ? 'No SKU prices yet — add a row or upload a CSV / Excel file.'
                                  : 'No SKUs match your search.'}
                              </TableCell>
                            </TableRow>
                          ) : filteredItems.map((item) => (
                            <TableRow key={item.id} hover sx={{ backgroundColor: item._dirty ? 'action.hover' : 'inherit' }}>
                              <TableCell>
                                {item._new ? (
                                  <ProductSkuPicker
                                    value={item.sku}
                                    onPick={(sku, name) => handleItemPatch(
                                      item.id,
                                      name == null ? { sku } : { sku, product_name: name }
                                    )}
                                  />
                                ) : (
                                  <TextField
                                    size="small" variant="standard"
                                    value={item.sku || ''}
                                    disabled
                                    fullWidth
                                    inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
                                  />
                                )}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color={item.product_name ? 'text.primary' : 'text.disabled'} sx={{ fontSize: 13 }}>
                                  {item.product_name || '—'}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <TextField
                                  size="small" variant="standard" type="number"
                                  value={item.unit_price ?? 0}
                                  onChange={(e) => handleItemChange(item.id, 'unit_price', e.target.value)}
                                  inputProps={{ step: 0.01, min: 0, style: { textAlign: 'right' } }}
                                />
                              </TableCell>
                              <TableCell>
                                <TextField
                                  size="small" variant="standard"
                                  value={item.notes || ''}
                                  onChange={(e) => handleItemChange(item.id, 'notes', e.target.value)}
                                  fullWidth placeholder="—"
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Tooltip title={item._new ? 'Remove unsaved row' : 'Delete'}>
                                  <IconButton size="small" onClick={() => handleDeleteItem(item)}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <NewPriceTierDialog
        open={dialogOpen}
        tier={dialogTier}
        onClose={() => setDialogOpen(false)}
        onSaved={async (tierId) => {
          setDialogOpen(false);
          await loadTiers();
          if (tierId) setSelectedTierId(tierId);
        }}
      />

      <Dialog open={uploadOpen} onClose={uploading ? undefined : () => setUploadOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Upload SKU prices
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            For tier: <strong>{tierDetail?.tier_name}</strong> ({tierDetail?.currency || 'currency not set'}). Existing SKUs will be updated; new SKUs will be added.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <PriceTierBulkUpload
            onChange={setUploadRows}
            currency={tierDetail?.currency || ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadOpen(false)} disabled={uploading}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleBulkUploadSubmit}
            disabled={uploadRows.length === 0 || uploading}
          >
            {uploading ? 'Uploading…' : `Upload ${uploadRows.length || ''} row${uploadRows.length === 1 ? '' : 's'}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/* ============================== BY-SKU VIEW ==============================
   One row per SKU across all tiers (Rishabh redesign 2026-07-03): product
   name/brand/category from the product master, tier count, per-currency
   price range, expandable to the full tier list with customer status.
   Server-side paging via /api/price-tiers/by-sku. */
function BySkuView({ onOpenTier }) {
  const navigate = useNavigate();
  const { brands } = useBrand();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [hideInactive, setHideInactive] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Expanded row state: sku -> { loading, tiers }
  const [expanded, setExpanded] = useState({});

  // Debounce the free-text search (same 400ms convention as the PO page).
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(0); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await listPriceTiersBySku({
          search: search || undefined,
          brand_id: brandFilter || undefined,
          category: categoryFilter || undefined,
          hide_inactive_customers: hideInactive ? '1' : undefined,
          limit: rowsPerPage,
          offset: page * rowsPerPage,
        });
        if (cancelled) return;
        setRows(res.rows || []);
        setTotal(Number(res.total) || 0);
        if (Array.isArray(res.categories) && res.categories.length) setCategories(res.categories);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.error || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [search, brandFilter, categoryFilter, hideInactive, page, rowsPerPage]);

  const toggleExpand = async (sku) => {
    if (expanded[sku]) { setExpanded((p) => { const n = { ...p }; delete n[sku]; return n; }); return; }
    setExpanded((p) => ({ ...p, [sku]: { loading: true, tiers: [] } }));
    try {
      const res = await getPriceTierSkuTiers(sku);
      setExpanded((p) => (p[sku] ? { ...p, [sku]: { loading: false, tiers: res.data || [] } } : p));
    } catch (e) {
      setExpanded((p) => (p[sku] ? { ...p, [sku]: { loading: false, tiers: [], error: e.message } } : p));
    }
  };

  const fmtRange = (r) => {
    const symbol = !r.currency ? '(currency not set) '
      : { USD: '$', GBP: '£', EUR: '€', AUD: 'A$', CAD: 'CA$', SGD: 'S$', NZD: 'NZ$', MYR: 'RM' }[r.currency] || `${r.currency} `;
    if (r.min === r.max) return `${symbol}${Number(r.min).toFixed(2)}`;
    return `${symbol}${Number(r.min).toFixed(2)} – ${symbol}${Number(r.max).toFixed(2)}`;
  };

  const brandName = (id) => (brands || []).find((b) => Number(b.id) === Number(id))?.name || (id ? `#${id}` : '—');

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      <Card>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Grid container spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
            <Grid item xs={12} md={4}>
              <TextField
                size="small" fullWidth placeholder="Search SKU or product name…"
                value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              />
            </Grid>
            <Grid item xs={6} md={2.5}>
              <FormControl fullWidth size="small">
                <InputLabel shrink>Brand</InputLabel>
                <Select
                  value={brandFilter} label="Brand" notched displayEmpty
                  onChange={(e) => { setBrandFilter(e.target.value); setPage(0); }}
                >
                  <MenuItem value="">All brands</MenuItem>
                  {[...(brands || [])]
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                    .map((b) => <MenuItem key={b.id} value={String(b.id)}>{b.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2.5}>
              <FormControl fullWidth size="small">
                <InputLabel shrink>Category</InputLabel>
                <Select
                  value={categoryFilter} label="Category" notched displayEmpty
                  onChange={(e) => { setCategoryFilter(e.target.value); setPage(0); }}
                >
                  <MenuItem value="">All categories</MenuItem>
                  {categories.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControlLabel
                sx={{ ml: 0 }}
                control={<Switch size="small" checked={hideInactive} onChange={(e) => { setHideInactive(e.target.checked); setPage(0); }} />}
                label={<Typography variant="caption" color="text.secondary">Hide inactive-customer tiers</Typography>}
              />
            </Grid>
          </Grid>

          {loading ? (
            <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 40 }} />
                    <TableCell>SKU</TableCell>
                    <TableCell>Product</TableCell>
                    <TableCell>Brand</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell align="right"># Tiers</TableCell>
                    <TableCell align="right">Price range</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No SKUs match the current filters.
                      </TableCell>
                    </TableRow>
                  ) : rows.map((r) => (
                    <React.Fragment key={r.sku}>
                      <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => toggleExpand(r.sku)}>
                        <TableCell>
                          <IconButton size="small">
                            {expanded[r.sku] ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 13 }}>{r.sku}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: 13 }} color={r.product_name ? 'text.primary' : 'text.disabled'}>
                            {r.product_name || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: 13 }}>{brandName(r.brand_id)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: 13 }} color={r.category ? 'text.primary' : 'text.disabled'}>
                            {r.category || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip label={r.n_tiers} size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontSize: 13 }}>
                            {(r.price_ranges || []).map(fmtRange).join(' · ')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={7} sx={{ p: 0, borderBottom: expanded[r.sku] ? undefined : 'none' }}>
                          <Collapse in={Boolean(expanded[r.sku])} unmountOnExit>
                            <Box sx={{ px: 6, py: 1.5, bgcolor: 'grey.50' }}>
                              {expanded[r.sku]?.error && (
                                <Alert severity="warning" sx={{ mb: 1 }}>
                                  Couldn't load this SKU's tiers: {expanded[r.sku].error}
                                </Alert>
                              )}
                              {/* Count/content consistency: the "# Tiers" chip
                                  respects the hide-inactive filter, so the
                                  expanded list must too — with a visible trace
                                  for what's hidden. */}
                              {(() => {
                                const all = expanded[r.sku]?.tiers || [];
                                const hiddenN = hideInactive
                                  ? all.filter(t => t.customers_active === false && (t.customer_names || []).length > 0).length
                                  : 0;
                                return hiddenN > 0 ? (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                    {hiddenN} inactive-customer tier{hiddenN > 1 ? 's' : ''} hidden (not in the count) — turn off the filter above to see {hiddenN > 1 ? 'them' : 'it'}
                                  </Typography>
                                ) : null;
                              })()}
                              {expanded[r.sku]?.loading ? (
                                <CircularProgress size={18} />
                              ) : (
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Tier</TableCell>
                                      <TableCell>Customers</TableCell>
                                      <TableCell align="right">Price</TableCell>
                                      <TableCell sx={{ width: 90 }} />
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {(expanded[r.sku]?.tiers || [])
                                      .filter(t => !hideInactive || !(t.customers_active === false && (t.customer_names || []).length > 0))
                                      .map((t) => (
                                      <TableRow key={t.tier_id} hover>
                                        <TableCell>
                                          <Typography variant="body2" fontWeight={500} sx={{ fontSize: 13 }}>{t.tier_name}</Typography>
                                          {t.tier_code && <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{t.tier_code}</Typography>}
                                        </TableCell>
                                        <TableCell>
                                          <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                                            {Array.from(new Set(t.customer_names || [])).slice(0, 4).map((n, i) => (
                                              <Chip
                                                key={`${n}-${i}`} label={n} size="small" variant="outlined"
                                                color={t.customers_active ? 'success' : 'default'}
                                                sx={{ height: 18, fontSize: 10, opacity: t.customers_active ? 1 : 0.6 }}
                                              />
                                            ))}
                                            {(t.customer_names || []).length === 0 && (
                                              <Typography variant="caption" color="text.secondary">generic tier</Typography>
                                            )}
                                            {t.customers_active === false && (
                                              <Typography variant="caption" color="text.secondary">(inactive)</Typography>
                                            )}
                                          </Stack>
                                        </TableCell>
                                        <TableCell align="right">
                                          <Typography variant="body2" fontWeight={600} sx={{ fontSize: 13 }}>
                                            {fmtRange({ currency: t.currency, min: Number(t.unit_price), max: Number(t.unit_price) })}
                                          </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                            <Tooltip title="Open this tier">
                                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); onOpenTier(t.tier_id); }}>
                                                <TierIcon fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Open product page">
                                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/inventory/cogs?product=${encodeURIComponent(r.sku)}&ptab=2`); }}>
                                                <OpenInNewIcon fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                          </Stack>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(e, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[25, 50, 100]}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
